// Utilidad para sincronizar abonos entre payments de lotes y expenses (movimientos)
// Uso: importar y usar en PaymentSystem, MovimientosTable, LotForm, etc.

export function syncCrearAbono({ lots, setLots, expenses, setExpenses, lotIds, abono, cobro }: any) {
  // Agregar abono a cada lote
  const nuevosLots = (lots as any[]).map((lot: any) => {
    if (lotIds.includes(lot.id)) {
      // Si ya existe un abono para esta cuota, actualizarlo; si no, agregarlo
      let pagoEditado = false;
      const pagos = (lot.payments || []).map((p: any) => {
        if (
          p.type === 'abono' &&
          p.cuotaNumero === abono.cuotaNumero &&
          p.cuotaFechaVencimiento === abono.cuotaFechaVencimiento
        ) {
          pagoEditado = true;
          return { ...abono };
        }
        return p;
      });
      return {
        ...lot,
        payments: pagoEditado ? pagos : [...pagos, abono],
        amountPaid: (pagos.filter((p: any) => p.type === 'abono').reduce((sum: number, p: any) => sum + Number(p.amount), 0) + (pagoEditado ? 0 : Number(abono.amount)))
      };
    }
    return lot;
  });
  // Agregar movimiento a expenses
  const referencia = `Transferencia: ${abono.depositSlip}, Boleta: ${abono.receipt}, Cuota: ${abono.cuotaNumero}`;
  const movimiento = {
    id: Date.now().toString(),
    concepto: cobro ? `Abono de ${cobro.comprador} para lote ${cobro.lotes} (Cuota ${abono.cuotaNumero})` : abono.concepto || 'Abono',
    responsable: cobro ? cobro.vendedor : abono.responsable || '',
    fecha: abono.date,
    monto: Number(abono.amount),
    estado: 'Pagado',
    referencia,
    tipo: 'ingreso',
    lotes: cobro ? cobro.lotes : lotIds.join(', '),
    vendedor: cobro ? cobro.vendedor : abono.vendedor || ''
  };
  const nuevosExpenses = [...(expenses as any[]).filter((e: any) => !(e.tipo === 'ingreso' && e.lotes === movimiento.lotes && e.referencia && e.referencia.includes(`Cuota: ${abono.cuotaNumero}`))), movimiento];
  setLots(nuevosLots);
  setExpenses(nuevosExpenses);
}

export function syncEliminarAbono({ lots, setLots, expenses, setExpenses, lotIds, cuotaNumero, cuotaFechaVencimiento, recibo, cobro }: any) {
  // Eliminar abono de payments de cada lote
  const nuevosLots = (lots as any[]).map((lot: any) => {
    if (lotIds.includes(lot.id)) {
      const pagos = (lot.payments || []).filter((p: any) => {
        return !(
          p.type === 'abono' &&
          p.cuotaNumero === cuotaNumero &&
          p.cuotaFechaVencimiento === cuotaFechaVencimiento &&
          (p.receipt === recibo || p.receiptNumber === recibo || !recibo || recibo === '0')
        );
      });
      return {
        ...lot,
        payments: pagos,
        amountPaid: pagos.filter((p: any) => p.type === 'abono').reduce((sum: number, p: any) => sum + Number(p.amount), 0)
      };
    }
    return lot;
  });
  // Eliminar movimiento correspondiente en expenses
  const nuevosExpenses = (expenses as any[]).filter((e: any) =>
    !(e.tipo === 'ingreso' && e.lotes === (cobro ? cobro.lotes : lotIds.join(', ')) && e.referencia && e.referencia.includes(`Cuota: ${cuotaNumero}`) && (e.referencia.includes(`Boleta: ${recibo}`) || !recibo || recibo === '0'))
  );
  setLots(nuevosLots);
  setExpenses(nuevosExpenses);
}

export function syncAbonosGlobal(lots: any[], setLots: any, expenses: any[], setExpenses: any) {
  // 1. Limpiar payments de lotes: solo dejar abonos que tengan par en expenses (lógica robusta)
  const nuevosLots = lots.map((lot: any) => {
    if (!lot.payments) return lot;
    const pagosSincronizados = lot.payments.filter((p: any) => {
      if (p.type !== 'abono') return true; // dejar enganche, contado, etc.
      return expenses.some((e: any) => {
        // Normalizar lotes a array
        let lotesExpense = e.lotes;
        if (typeof lotesExpense === 'string') lotesExpense = [lotesExpense];
        if (!Array.isArray(lotesExpense)) lotesExpense = [];
        const montoIgual = Math.abs(Number(e.monto) - Number(p.amount)) < 1;
        const tipoIngreso = e.tipo === 'ingreso';
        const fechaPago = new Date(p.date);
        const fechaExpense = new Date(e.fecha);
        const diffDias = Math.abs((fechaExpense.getTime() - fechaPago.getTime()) / (1000 * 60 * 60 * 24));
        const fechaIgual = diffDias <= 1;
        const referenciaIncluye = (e.referencia && (p.cuotaNumero ? e.referencia.includes(p.cuotaNumero) : false)) || (e.referencia && p.receipt && e.referencia.includes(p.receipt));
        const loteCoincide = lotesExpense.includes(lot.id?.toString());
        return montoIgual && tipoIngreso && fechaIgual && referenciaIncluye && loteCoincide;
      });
    });
    return {
      ...lot,
      payments: pagosSincronizados,
      amountPaid: pagosSincronizados.filter((p: any) => p.type === 'abono').reduce((sum: number, p: any) => sum + Number(p.amount), 0)
    };
  });
  // 2. Limpiar expenses: solo dejar abonos que tengan par en algún lote (lógica robusta)
  const nuevosExpenses = expenses.filter((e: any) => {
    if (e.tipo !== 'abono' && e.tipo !== 'ingreso') return true;
    // Buscar en todos los lotes si existe el abono (lógica robusta)
    return nuevosLots.some((lot: any) => (lot.payments || []).some((p: any) => {
      if (p.type !== 'abono') return false;
      let lotesExpense = e.lotes;
      if (typeof lotesExpense === 'string') lotesExpense = [lotesExpense];
      if (!Array.isArray(lotesExpense)) lotesExpense = [];
      const montoIgual = Math.abs(Number(e.monto) - Number(p.amount)) < 1;
      const tipoIngreso = e.tipo === 'ingreso';
      const fechaPago = new Date(p.date);
      const fechaExpense = new Date(e.fecha);
      const diffDias = Math.abs((fechaExpense.getTime() - fechaPago.getTime()) / (1000 * 60 * 60 * 24));
      const fechaIgual = diffDias <= 1;
      const referenciaIncluye = (e.referencia && (p.cuotaNumero ? e.referencia.includes(p.cuotaNumero) : false)) || (e.referencia && p.receipt && e.referencia.includes(p.receipt));
      const loteCoincide = lotesExpense.includes(lot.id?.toString());
      return montoIgual && tipoIngreso && fechaIgual && referenciaIncluye && loteCoincide;
    }));
  });
  setLots(nuevosLots);
  setExpenses(nuevosExpenses);
}

// Puedes agregar syncEditarAbono si necesitas lógica especial para edición, pero crear+eliminar suele ser suficiente. 